import styles from './styles.less';
import classNames from 'classnames';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import { shortFormat, defaultFormat } from '@graphistry/viz/src/formatters';
import { ColorPill } from '@graphistry/viz/src/components/color-pill/colorPill';

const defaultFilterBounds = { leftest: false, rightest: false };
export const SparklineBar = ({
    color = false,
    componentType,
    index,
    name,
    binType,
    dataType,
    style,
    values = [],
    min,
    max,
    binWidth = `20px`,
    isMasked = false,
    filtered = false,
    maskedCount = 0,
    maskedHeight = `50%`,
    globalCount = 0,
    globalHeight = `100%`,
    filteredHeight = `50%`,
    filteredCount = 0,
    tooltipPlacement = 'top',
    onBinTouchMove,
    onBinTouchStart,
    onBinTouchCancel,
    filterBounds = defaultFilterBounds,
    ...restSparklineProps
}) => {
    const { leftest, rightest } = filterBounds;

    return (
        <OverlayTrigger
            trigger={['hover', 'focus']}
            placement={tooltipPlacement}
            overlay={
                <Popover id={`histogram-${name}-tooltip`} style={{ zIndex: 999999999 }}>
                    <SparklineBarInfo
                        color={color}
                        values={values}
                        binType={binType}
                        dataType={dataType}
                        globalCount={globalCount}
                        maskedCount={maskedCount}
                        filteredCount={filteredCount}
                    />
                </Popover>
            }>
            <div
                {...restSparklineProps}
                style={{ width: binWidth, ...style }}
                className={classNames({
                    [styles['column']]: true,
                    [styles['leftest']]: leftest,
                    [styles['rightest']]: rightest,
                    [styles['is-masked']]: isMasked,
                    [styles['is-filtered']]: filtered,
                    [styles['is-colored']]: Boolean(color),
                    [styles['is-partially-filtered']]: filteredCount !== globalCount,
                    [styles['is-empty-global']]: !globalCount
                })}
                onTouchMove={onBinTouchMove}
                onMouseMove={onBinTouchMove}
                onMouseDown={onBinTouchStart}
                onTouchStart={onBinTouchStart}
                onTouchCancel={onBinTouchCancel}>
                <div
                    style={{ backgroundColor: color || 'transparent' }}
                    className={classNames({
                        [styles['bar-bg']]: true,
                        [styles['bar-rect']]: true,
                        [styles['bar-bg-bg']]: true
                    })}
                />
                <div
                    className={classNames({
                        [styles['bar-bg']]: true,
                        [styles['bar-rect']]: true,
                        [styles['bar-bg-fade']]: true
                    })}
                />
                <div
                    className={classNames({
                        [styles['bar-rect']]: true,
                        [styles['bar-global']]: true
                    })}
                    style={{
                        bottom: 0,
                        height: globalHeight,
                        visibility: globalCount ? 'visible' : 'hidden',
                        backgroundColor: color || '#EEE'
                    }}
                />
                <div
                    className={classNames({
                        [styles['bar-rect']]: true,
                        [styles['bar-filtered']]: true
                    })}
                    style={{
                        bottom: 0,
                        height: filteredHeight,
                        visibility: filteredCount ? 'visible' : 'hidden',
                        backgroundColor: color || '#BBB'
                    }}
                />
                <div
                    className={classNames({
                        [styles['bar-rect']]: true,
                        [styles['bar-masked']]: true
                    })}
                    style={{
                        bottom: 0,
                        height: maskedHeight,
                        visibility: maskedCount ? 'visible' : 'hidden',
                        backgroundColor: 'rgb(15, 165, 197)'
                    }}
                />
                <div
                    className={classNames({
                        [styles['bar-bg']]: true,
                        [styles['bar-rect']]: true,
                        [styles['bar-bg-border']]: true
                    })}
                />
            </div>
        </OverlayTrigger>
    );
};

const SparklineBarInfo = ({ values, min, max, color, binType, dataType, globalCount, maskedCount, filteredCount }) => {
    const rows = [];

    //console.info('countby', {values, min, max, color, binType, dataType, globalCount, maskedCount, filteredCount});

    if (!Array.isArray(values)) {
        values = [];
    }

    if (globalCount === filteredCount) {
        rows.push([
            <p key="count-label">TOTAL</p>,
            <p key="count-value">{globalCount}</p>
        ]);
    } else {
        rows.push(
            [<p key="count-label">TOTAL</p>,<p key="count-value">{globalCount}</p>],
            [<p key="count-label">VISIBLE</p>, <p key="count-value">{filteredCount}</p>]
        );
    }

    if (maskedCount) {
        rows.push([
            <p key="selected-label" style={{ color: '#ff6600' }}>SELECTED</p>,
            <p key="selected-value" style={{ color: '#ff6600' }}>{maskedCount}</p>
        ]);
    }

    if (values.length) {
        if (binType === 'countby') {
            rows.push([
                <p key="value-label">CATEGORY</p>,
                <p
                    key="value-value"
                    dangerouslySetInnerHTML={{
                        __html: defaultFormat(values[0], dataType)
                    }}
                />
            ]);
        } else if (binType === 'time') {
            const rangeSpans = values
                .slice(0, 2)
                .map(x => <span className={styles['nowrap']}>{shortFormat(x, dataType)}</span>);
            rows.push([
                <p key="value-label">RANGE</p>,
                <p key="value-value" className={styles['wrap']}>
                    {rangeSpans[0]}&#160;: {rangeSpans[1]}
                </p>
            ]);
        } else if (binType === 'histogram') {
            if (values.length === 1) {
                rows.push([
                    <p key="value-label">VALUE</p>,
                    <p key="value-value">{shortFormat(values[0], dataType)}</p>
                ]);
            } else if (values.length === 2) {
                const rangeSpans = values.map(x => (
                    <span className={styles['nowrap']}>{shortFormat(x, dataType)}</span>
                ));
                rows.push([
                    <p key="value-label">RANGE</p>,
                    <p key="value-value" className={styles['wrap']}>
                        {rangeSpans[0]}&#160;: {rangeSpans[1]}
                    </p>
                ]);
            }
        }
    }

    if (globalCount && color) {
        rows.push([
            <p key="color-label">Color</p>,
            <p key="color-value">
                {color}
                <ColorPill color={color} />
            </p>
        ]);
    }

    return (
        <table className={styles['histogram-info']}>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i} style={{ verticalAlign: 'middle' }}>
                        <td key="label" style={{ textAlign: 'right' }}>
                            {row[0]}
                        </td>
                        <td key="value" style={{ textAlign: 'left', paddingLeft: 10 }}>
                            {row[1]}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
